/**
 * usePricing Hook - Real-time pricing from Stripe
 *
 * Fetches subscription plan prices and additional seat pricing
 * directly from Stripe via the backend API with caching.
 *
 * Features:
 * - Real-time prices from Stripe
 * - 1-hour cache TTL (matches backend)
 * - Automatic fallback to default prices
 * - Formatted price strings
 */

import { useQuery } from '@tanstack/react-query';

// ============================================================================
// TYPES
// ============================================================================

interface PlanPrice {
  price: number;
  formatted: string;
}

interface PricingData {
  plans: {
    starter: PlanPrice;
    pro: PlanPrice;
    grow: PlanPrice;
  };
  additionalSeat: PlanPrice;
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook for accessing real-time subscription pricing from Stripe
 *
 * @example
 * function PricingPage() {
 *   const { pricing, isLoading } = usePricing();
 *
 *   if (isLoading) return <Loading />;
 *
 *   return (
 *     <div>
 *       <p>Pro Plan: {pricing.plans.pro.formatted}</p>
 *       <p>Additional Seat: {pricing.additionalSeat.formatted}</p>
 *     </div>
 *   );
 * }
 */
export function usePricing() {
  const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8080";

  // Fetch pricing data from backend
  const query = useQuery<PricingData, Error>({
    queryKey: ['pricing', 'plans'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}/api/workflows/pricing/plans`, {
        // No credentials needed - public endpoint
      });

      if (!response.ok) {
        throw new Error('Failed to fetch pricing data');
      }

      const result = await response.json();

      // API returns { success: true, data: { plans, additionalSeat } }
      if (!result.success || !result.data) {
        throw new Error('Invalid pricing response from server');
      }

      return result.data;
    },
    staleTime: 60 * 60 * 1000, // 1 hour (matches backend cache)
    cacheTime: 60 * 60 * 1000, // 1 hour
    refetchOnWindowFocus: false, // Prices don't change that often
    retry: 2, // Retry twice on failure
    // Provide fallback data while loading or on error
    placeholderData: {
      plans: {
        starter: { price: 0, formatted: 'Free' },
        pro: { price: 29, formatted: '$29/month' },
        grow: { price: 79, formatted: '$79/month' }
      },
      additionalSeat: { price: 5, formatted: '$5/seat/month' }
    }
  });

  // Helper: Get price for a specific plan
  const getPlanPrice = (planName: 'starter' | 'pro' | 'grow'): number => {
    return query.data?.plans[planName]?.price ?? 0;
  };

  // Helper: Get formatted price for a specific plan
  const getPlanPriceFormatted = (planName: 'starter' | 'pro' | 'grow'): string => {
    return query.data?.plans[planName]?.formatted ?? 'Free';
  };

  // Helper: Get additional seat price
  const getSeatPrice = (): number => {
    return query.data?.additionalSeat?.price ?? 5;
  };

  // Helper: Get formatted additional seat price
  const getSeatPriceFormatted = (): string => {
    return query.data?.additionalSeat?.formatted ?? '$5/seat/month';
  };

  return {
    // Query state
    pricing: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,

    // Helper methods
    getPlanPrice,
    getPlanPriceFormatted,
    getSeatPrice,
    getSeatPriceFormatted,

    // Convenient aliases for direct access
    plans: query.data?.plans,
    seatPrice: query.data?.additionalSeat
  };
}
